import { FC } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  DropdownTemplate,
  NumberTemplate,
  TemplateWrapper,
  TextboxTemplate,
} from "@ryde/controls";
import type {
  IApiKeyCreateDrawerProps,
  IInjectedDrawerProps,
} from "@ryde/shared";

import { CommonDefaultDrawer } from "../common-default-drawer";
import {
  BaseDrawer,
  DrawerFormWrapperStyled,
  DrawerHeader,
  FormFooter,
} from "../drawer";
import { ApiKeyCreateForm } from "./api-key-create-drawer.models";
import { useApiKeyCreateDrawer } from "./use-api-key-create-drawer";

export const ApiKeyCreateDrawer: FC<
  IApiKeyCreateDrawerProps & IInjectedDrawerProps
> = ({ onCloseDrawer }) => {
  const { t } = useTranslation();
  const {
    methods,
    companyId,
    companyItems,
    roleItems,
    isLoadingCompanies,
    isLoadingRoles,
    isCreating,
    isCreateDisabled,
    onCreateClick,
  } = useApiKeyCreateDrawer();

  return (
    <BaseDrawer onCloseDrawer={onCloseDrawer}>
      <CommonDefaultDrawer
        className="drawer-form"
        onCloseDrawer={onCloseDrawer}
        Header={
          <DrawerHeader
            title={t("apiKey.actions.create")}
            onCloseDrawer={onCloseDrawer}
          />
        }
        Footer={
          <FormFooter
            cancelLabel={t("general.CLOSE")}
            onCancelClick={onCloseDrawer}
            onSaveClick={onCreateClick}
            saveLabel={
              isCreating
                ? t("apiKey.actions.creating")
                : t("apiKey.actions.createSubmit")
            }
            isSaveDisabled={isCreateDisabled}
          />
        }
      >
        <FormProvider {...methods}>
          <DrawerFormWrapperStyled>
            <TemplateWrapper>
              <DropdownTemplate
                name={ApiKeyCreateForm.companyId}
                formLabel={t("apiKey.formLabel.companyId")}
                formAttributes={{
                  selectItems: companyItems,
                  disabled: isLoadingCompanies,
                  skipDropdownItemsTranslation: true,
                }}
              />
            </TemplateWrapper>
            <TemplateWrapper>
              <TextboxTemplate
                name={ApiKeyCreateForm.name}
                formLabel={t("apiKey.formLabel.name")}
              />
            </TemplateWrapper>
            <TemplateWrapper>
              <DropdownTemplate
                name={ApiKeyCreateForm.roleId}
                formLabel={t("apiKey.formLabel.roleId")}
                formAttributes={{
                  selectItems: roleItems,
                  disabled: !companyId || isLoadingRoles,
                  skipDropdownItemsTranslation: true,
                }}
              />
            </TemplateWrapper>
            <TemplateWrapper>
              {/* intRounding blocks the decimal keys: a key lives whole days */}
              <NumberTemplate
                name={ApiKeyCreateForm.ttlDays}
                formLabel={t("apiKey.formLabel.ttlDays")}
                formAttributes={{ intRounding: true }}
              />
            </TemplateWrapper>
          </DrawerFormWrapperStyled>
        </FormProvider>
      </CommonDefaultDrawer>
    </BaseDrawer>
  );
};
